import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Play, Pause, Timer as TimerIcon, ChevronRight } from 'lucide-react';
import { Button } from '@/components/button';
import { Select } from '@/components/select';
import { Input } from '@/components/input';
import { useClients } from '@/lib/clients';
import {
  useRunningTimer,
  useStartTimer,
  useStopTimer,
  formatDuration,
} from '@/lib/time-entries';
import type { TimeEntryData } from '@/types/api';
import { useFormError } from '@/lib/form-error';
import { cn } from '@/lib/cn';

export function TimerPill() {
  const { data } = useRunningTimer();
  // Treat a payload without `started_at` as "no timer" — defends against the
  // API ever shipping an empty object instead of an explicit null.
  const running = data && data.started_at ? data : null;
  const stop = useStopTimer();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // tick exists only to force re-render each second; the value is unused.
  void tick;
  const liveSeconds = running
    ? Math.max(0, Math.floor((Date.now() - new Date(running.started_at).getTime()) / 1000))
    : 0;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cn(
          'flex h-9 items-center gap-2 rounded-md border border-[var(--color-sidebar-border)] px-2.5 text-sm transition-colors',
          running
            ? 'bg-[color-mix(in_oklab,var(--color-success)_12%,transparent)] text-[var(--color-success)] hover:bg-[color-mix(in_oklab,var(--color-success)_18%,transparent)]'
            : 'bg-[var(--color-sidebar-accent)] text-[var(--color-sidebar-foreground)] hover:bg-[var(--color-sidebar-border)]',
        )}
      >
        <TimerIcon className="size-4" />
        <span className="tabular-nums">
          {running ? formatDuration(liveSeconds) : 'Start timer'}
        </span>
      </button>

      {open ? (
        <div
          role="dialog"
          className="absolute right-0 z-30 mt-1 w-80 overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated)] text-[var(--color-popover-foreground)] shadow-lg"
        >
          {running ? (
            <RunningPanel
              running={running}
              onStop={async () => {
                await stop.mutateAsync();
                setOpen(false);
              }}
              onClose={() => setOpen(false)}
            />
          ) : (
            <StartPanel onClose={() => setOpen(false)} />
          )}
          <Link
            to="/time"
            onClick={() => setOpen(false)}
            className="flex items-center justify-between border-t border-[var(--color-border)] px-3 py-2 text-xs text-[var(--color-foreground-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-foreground)]"
          >
            All time entries
            <ChevronRight className="size-3.5" />
          </Link>
        </div>
      ) : null}
    </div>
  );
}

function RunningPanel({
  running,
  onStop,
  onClose,
}: {
  running: TimeEntryData;
  onStop: () => Promise<void>;
  onClose: () => void;
}) {
  const elapsed =
    Math.floor((Date.now() - new Date(running.started_at).getTime()) / 1000);
  return (
    <div className="space-y-3 p-3">
      <div>
        <div className="text-xs uppercase tracking-wide text-[var(--color-foreground-subtle)]">
          Now tracking
        </div>
        <div className="mt-0.5 font-medium">{running.client_name ?? 'Untitled client'}</div>
        {running.description ? (
          <div className="text-xs text-[var(--color-foreground-muted)]">{running.description}</div>
        ) : null}
        <div className="mt-1 text-2xl font-semibold tabular-nums text-[var(--color-success)]">
          {formatDuration(elapsed)}
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="secondary" className="flex-1" onClick={onClose}>
          Keep running
        </Button>
        <Button variant="danger" className="flex-1" onClick={onStop}>
          <Pause className="size-4" /> Stop
        </Button>
      </div>
    </div>
  );
}

function StartPanel({ onClose }: { onClose: () => void }) {
  const clients = useClients({ status: 'active' });
  const start = useStartTimer();
  const { error, wrap } = useFormError();
  const [clientId, setClientId] = useState<string>('');
  const [description, setDescription] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!clientId) return;
    const ok = await wrap(() =>
      start.mutateAsync({
        client_id: Number(clientId),
        description: description || undefined,
      }),
    );
    if (ok) {
      setClientId('');
      setDescription('');
      onClose();
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 p-3">
      <div>
        <label className="block space-y-1 text-xs">
          <span className="font-medium text-[var(--color-foreground-muted)]">Client</span>
          <Select value={clientId} onChange={(e) => setClientId(e.target.value)} required>
            <option value="">Pick a client…</option>
            {(clients.data?.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </Select>
        </label>
      </div>
      <Input
        placeholder="What are you working on? (optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
      <Button type="submit" className="w-full" disabled={start.isPending || !clientId}>
        <Play className="size-4" /> {start.isPending ? 'Starting…' : 'Start timer'}
      </Button>
    </form>
  );
}
